import React, { useEffect, useRef } from "react";
import { useReducedMotion } from "@/components/cosmic/useReducedMotion";

export interface UseCanvasLoopOptions {
  running: boolean;
  maxDpr?: number;
}

export type DrawCallback = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  timeMs: number,
  deltaMs: number
) => void;

/**
 * Manages an accessible, performant HTML5 canvas animation loop.
 * - Handles ResizeObserver sizing and devicePixelRatio scaling (capped at 2 desktop, 1.5 mobile).
 * - Pauses automatically when tab is hidden or running is false.
 * - Respects prefers-reduced-motion.
 * - Cleans up listeners and rAF IDs on unmount.
 */
export function useCanvasLoop(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  draw: DrawCallback,
  opts: UseCanvasLoopOptions
): void {
  const { running, maxDpr } = opts;
  const reducedMotion = useReducedMotion();
  const drawRef = useRef<DrawCallback>(draw);
  drawRef.current = draw;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number | null = null;
    let lastTime = 0;
    let isVisible = typeof document !== "undefined" ? !document.hidden : true;
    let logicalWidth = canvas.clientWidth || 300;
    let logicalHeight = canvas.clientHeight || 150;

    const updateSize = () => {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const w = Math.floor(rect.width);
      const h = Math.floor(rect.height);

      if (w <= 0 || h <= 0) return;

      const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
      const rawDpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
      const cap = maxDpr ?? (isMobile ? 1.5 : 2);
      const dpr = Math.min(rawDpr, cap);

      logicalWidth = w;
      logicalHeight = h;

      const physicalWidth = Math.floor(w * dpr);
      const physicalHeight = Math.floor(h * dpr);

      if (canvas.width !== physicalWidth || canvas.height !== physicalHeight) {
        canvas.width = physicalWidth;
        canvas.height = physicalHeight;
      }

      ctx.resetTransform?.();
      ctx.scale(dpr, dpr);
    };

    updateSize();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateSize();
        // Trigger one frame draw on resize even if not looping
        drawRef.current(ctx, logicalWidth, logicalHeight, lastTime, 0);
      });
      if (canvas.parentElement) {
        resizeObserver.observe(canvas.parentElement);
      } else {
        resizeObserver.observe(canvas);
      }
    }

    const onVisibilityChange = () => {
      isVisible = !document.hidden;
      if (isVisible && running && !reducedMotion) {
        lastTime = performance.now();
        loop(lastTime);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const loop = (currentTime: number) => {
      if (!running || !isVisible) {
        animationFrameId = null;
        return;
      }

      const delta = lastTime === 0 ? 0 : Math.min(currentTime - lastTime, 100);
      lastTime = currentTime;

      drawRef.current(ctx, logicalWidth, logicalHeight, currentTime, delta);

      if (!reducedMotion) {
        animationFrameId = requestAnimationFrame(loop);
      } else {
        animationFrameId = null;
      }
    };

    // Render initial frame
    lastTime = performance.now();
    drawRef.current(ctx, logicalWidth, logicalHeight, lastTime, 0);

    if (running && isVisible && !reducedMotion) {
      animationFrameId = requestAnimationFrame(loop);
    }

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [canvasRef, running, maxDpr, reducedMotion]);
}
